import axios from 'axios';

import { MNSproCloudApi } from '../..';
import {
  PaginatedOfUserGroupsDeviceResponse,
  UserGroupCreateRequestBody,
  UserGroupMergeRequestBody,
  UserGroupResponse,
  UserGroupsDeviceResponse,
  UserGroupTemplateResponse,
  UserGroupUpdateRequestBody,
  UserGroupUserResponse,
} from '../api/v2';

export class GroupManagementService {
  private static getApi(authToken: string) {
    const config = MNSproCloudApi.V2.createConfiguration(authToken);
    const api = MNSproCloudApi.V2.UserGroupsApiFactory(config);
    return api;
  }

  /**
   * Retrieves all existing groups.
   */
  static async getAll(
    authToken: string,
    organizationId: string,
    period: string,
  ): Promise<UserGroupResponse[]> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.getAll(
      organizationId,
      undefined,
      undefined,
      period,
    );

    return response.data;
  }

  static async get(
    authToken: string,
    organizationId: string,
    groupId: string,
  ): Promise<UserGroupResponse> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.get(groupId, organizationId);
    return response.data;
  }

  /**
   * Creates a new group.
   */
  static async create(
    authToken: string,
    organizationId: string,
    userGroupCreateRequest: UserGroupCreateRequestBody,
  ): Promise<UserGroupResponse> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.create(organizationId, userGroupCreateRequest);

    return response.data;
  }

  static async getGroup(
    authToken: string,
    organizationId: string,
    groupId: string,
  ): Promise<UserGroupResponse> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.get(groupId, organizationId);

    return response.data;
  }

  static async deleteGroup(
    authToken: string,
    organizationId: string,
    groupId: string,
  ) {
    const api = GroupManagementService.getApi(authToken);
    await api._delete(groupId, organizationId);
  }

  static async getGroupTemplates(
    authToken: string,
    organizationId: string,
  ): Promise<UserGroupTemplateResponse[]> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.getGroupTemplatets(organizationId);

    return response.data;
  }

  static async getGroupsDeleted(
    authToken: string,
    organizationId: string,
  ): Promise<UserGroupResponse[]> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.getDeleted(organizationId);

    return response.data;
  }

  static async restoreGroup(
    authToken: string,
    organizationId: string,
    groupId: string,
  ) {
    const api = GroupManagementService.getApi(authToken);
    const _response = await api.restoreDeleted(organizationId, groupId);
  }

  static async getUsers(
    authToken: string,
    organizationId: string,
    period: string,
  ): Promise<UserGroupUserResponse[]> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.getPermittedUsersForUser(organizationId, period);

    return response.data;
  }

  static async getDevices(
    authToken: string,
    organizationId: string,
  ): Promise<UserGroupsDeviceResponse[]> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.getAllDevices(organizationId);
    const x = response.data as any as PaginatedOfUserGroupsDeviceResponse;
    let nextLink = x.nextLink;
    const { value, totalCount } = x;
    let deviceResponses: UserGroupsDeviceResponse[] = value;
    while (nextLink) {
      const pageResponse = await axios.get<PaginatedOfUserGroupsDeviceResponse>(
        nextLink,
        {
          headers: {
            authorization: 'Bearer ' + authToken,
          },
        },
      );
      deviceResponses = deviceResponses.concat(pageResponse.data.value);
      nextLink = pageResponse.data.nextLink;
    }

    return deviceResponses;
  }

  static async updateGroup(
    authToken: string,
    organizationId: string,
    userGroupUpdateRequest: UserGroupUpdateRequestBody,
  ): Promise<UserGroupResponse> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.update(organizationId, userGroupUpdateRequest);

    return response.data;
  }

  static async repairOneNoteNotebook(
    authToken: string,
    organizationId: string,
    groupId: string,
  ): Promise<boolean> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.repairNotebook(groupId, organizationId);

    return response.data;
  }

  static async mergeGroups(
    authToken: string,
    organizationId: string,
    userGroupMergeRequest: UserGroupMergeRequestBody,
  ): Promise<UserGroupResponse> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.merge(organizationId, userGroupMergeRequest);

    return response.data;
  }

  /**
   * Retrieves all archived groups of the specified organization.
   */
  static async getArchived(
    authToken: string,
    organizationId: string,
    filterBy?: string,
    pageSize?: number,
    page?: number,
  ): Promise<UserGroupResponse[]> {
    const api = GroupManagementService.getApi(authToken);
    const response = await api.getArchived(
      organizationId,
      filterBy,
      pageSize,
      page,
    );

    return response.data.items ?? [];
  }

  /**
   * Archives the specified groups.
   */
  static async archive(
    authToken: string,
    organizationId: string,
    groupIds: string[],
  ): Promise<string> {
    const api = GroupManagementService.getApi(authToken);
    const taskId = await api.archive(organizationId, groupIds);
    return taskId.data;
  }

  /**
   * Restores the specified groups from archive.
   */
  static async restoreFromArchive(
    authToken: string,
    organizationId: string,
    groupIds: string[],
  ): Promise<string> {
    const api = GroupManagementService.getApi(authToken);
    const taskId = await api.restoreFromArchive(organizationId, groupIds);
    return taskId.data;
  }
}
